import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Send,
  Sparkles,
  User,
  Image as ImageIcon,
  CheckCircle,
  RefreshCw,
  CheckCircle2,
  SlidersHorizontal,
} from 'lucide-react';
import { ChatMessage, ImageSource, PhotoAnalysis, QueueItemStatus } from '../types';
import { buildQuickActions } from '../services/quickActions';

interface ChatFollowUpProps {
  messages: ChatMessage[];
  onSendMessage: (text: string, referenceImage?: ImageSource) => void;
  isProcessing: boolean;
  status?: QueueItemStatus;
  hasRestoredVersions?: boolean;
  analysisDirty?: boolean;
  analysis?: PhotoAnalysis | null;
  onApproveInitialRestoration?: (initialInstruction?: string, referenceImage?: ImageSource) => void;
  onRerenderWithUpdatedSettings?: () => void;
}

export const ChatFollowUp: React.FC<ChatFollowUpProps> = ({
  messages,
  onSendMessage,
  isProcessing,
  status = 'ready_for_review',
  hasRestoredVersions = false,
  analysisDirty = false,
  analysis = null,
  onApproveInitialRestoration,
  onRerenderWithUpdatedSettings,
}) => {
  const [inputText, setInputText] = useState('');
  const [referenceImage, setReferenceImage] = useState<ImageSource | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isFirstRun = !hasRestoredVersions;

  const quickActions = useMemo(
    () => buildQuickActions(analysis, { isFirstRun }),
    [analysis, isFirstRun]
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isProcessing]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isProcessing) return;

    if (isFirstRun && onApproveInitialRestoration) {
      onApproveInitialRestoration(inputText.trim() || undefined, referenceImage || undefined);
      setInputText('');
      setReferenceImage(null);
    } else {
      if (!inputText.trim() && !referenceImage) return;
      onSendMessage(inputText.trim(), referenceImage || undefined);
      setInputText('');
      setReferenceImage(null);
    }
  };

  const handleQuickAction = (instruction: string) => {
    if (isProcessing) return;
    if (isFirstRun && onApproveInitialRestoration) {
      onApproveInitialRestoration(instruction, referenceImage || undefined);
      setReferenceImage(null);
    } else {
      onSendMessage(instruction, referenceImage || undefined);
      setReferenceImage(null);
    }
  };

  const handleReferenceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const resultStr = reader.result as string;
      const base64Data = resultStr.split(',')[1];
      setReferenceImage({
        dataUrl: resultStr,
        base64Data,
        mimeType: file.type || 'image/jpeg',
        fileName: file.name,
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const isDigital =
    analysis?.photoType === 'digital-photo' || analysis?.sourceType === 'native-digital';

  return (
    <div className="kin-studio-panel flex flex-col h-[min(540px,70vh)] min-h-[420px] shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div
            className={`w-2.5 h-2.5 rounded-full ${
              isFirstRun ? 'bg-teal-500' : 'bg-emerald-500 animate-pulse'
            }`}
          />
          <h3 className="text-sm font-semibold text-slate-900">
            {isFirstRun ? 'Restoration Consultation & Approval' : 'Specialist Consultation & Follow-up'}
          </h3>
        </div>
        <span className="text-[11px] text-slate-500 font-mono">
          {isFirstRun ? 'Approval Console' : 'Conversational Fine-Tuning'}
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 p-4 overflow-y-auto space-y-3 text-xs bg-white">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex items-start gap-2.5 ${
              msg.sender === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            {msg.sender === 'agent' && (
              <div className="w-6 h-6 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0 mt-0.5">
                <Sparkles className="w-3.5 h-3.5" />
              </div>
            )}

            <div
              className={`max-w-[85%] rounded-xl px-3.5 py-2.5 leading-relaxed shadow-sm ${
                msg.sender === 'user'
                  ? 'bg-teal-600 text-white rounded-tr-none'
                  : 'bg-slate-100 border border-slate-200 text-slate-800 rounded-tl-none'
              }`}
            >
              {msg.text}

              {msg.actionTaken && (
                <div className="mt-2 pt-1.5 border-t border-slate-300/60 flex items-center gap-1.5 text-[11px] text-emerald-700 font-medium">
                  <CheckCircle className="w-3 h-3 shrink-0" />
                  <span>{msg.actionTaken}</span>
                </div>
              )}
            </div>

            {msg.sender === 'user' && (
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 shrink-0 mt-0.5">
                <User className="w-3.5 h-3.5" />
              </div>
            )}
          </div>
        ))}

        {isProcessing && (
          <div className="flex items-start gap-2.5">
            <div className="w-6 h-6 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0 animate-spin">
              <RefreshCw className="w-3.5 h-3.5" />
            </div>
            <div className="bg-slate-100 border border-slate-200 rounded-xl px-3.5 py-2 text-slate-600 italic flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-teal-500 animate-ping" />
              {isFirstRun
                ? 'Executing restoration with confirmed identity & fidelity rules...'
                : 'Applying requested adjustments with identity preservation...'}
            </div>
          </div>
        )}
      </div>

      {analysisDirty && hasRestoredVersions && !isProcessing && onRerenderWithUpdatedSettings && (
        <div className="mx-3 mb-2 kin-analysis-dirty">
          <p>
            <strong>Identity settings updated.</strong> Generate an updated restoration from the
            original source using the corrected subject settings.
          </p>
          <button
            type="button"
            className="kin-btn kin-btn-primary text-xs py-1.5 px-3"
            onClick={onRerenderWithUpdatedSettings}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Re-render with Updated Settings
          </button>
        </div>
      )}

      {isFirstRun && !isProcessing && (
        <div className="mx-3 mb-2 p-2.5 bg-teal-50 border border-teal-200 rounded-xl flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center space-x-2 text-teal-900">
            <SlidersHorizontal className="w-4 h-4 shrink-0 text-teal-700" />
            <span className="text-[11px] leading-tight">
              Analysis verified. Click <strong>Approve &amp; Restore</strong> below. (Optional notes
              can be added).
            </span>
          </div>
        </div>
      )}

      <div className="px-3 py-2 bg-slate-50 border-t border-slate-200">
        <div className="text-[10px] text-slate-600 mb-1.5 font-medium flex items-center justify-between">
          <span>{isFirstRun ? 'ONE-CLICK RESTORATION EMPHASIS:' : 'ONE-CLICK REFINEMENTS:'}</span>
          <span className="text-[10px] text-slate-400">Click to apply</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {quickActions.map((action, idx) => (
            <button
              key={action.id}
              type="button"
              disabled={isProcessing}
              title={action.instruction}
              onClick={() => handleQuickAction(action.instruction)}
              className={`text-[11px] px-2.5 py-1 rounded-full transition-colors border focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 disabled:opacity-100 disabled:text-slate-400 disabled:border-slate-200 disabled:bg-slate-50 ${
                idx === 0
                  ? 'bg-teal-50 hover:bg-teal-100 border-teal-300 text-teal-900'
                  : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700 hover:text-teal-800'
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {referenceImage && (
        <div className="px-3 py-1.5 bg-teal-50 border-t border-teal-200 flex items-center justify-between text-xs text-teal-900">
          <div className="flex items-center space-x-2 truncate">
            <img
              src={referenceImage.dataUrl}
              alt="Ref thumbnail"
              className="w-6 h-6 object-cover rounded border border-teal-300"
            />
            <span className="truncate text-[11px]">
              Optional Reference Attached: {referenceImage.fileName} (Identity check only)
            </span>
          </div>
          <button
            type="button"
            onClick={() => setReferenceImage(null)}
            className="text-slate-500 hover:text-slate-800 text-xs ml-2 font-bold px-1"
          >
            ✕
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="p-3 bg-white border-t border-slate-200 flex items-center gap-2"
      >
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          onChange={handleReferenceUpload}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          title="Add optional clear reference photo for identity preservation"
          className={`p-2 rounded-lg border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 ${
            referenceImage
              ? 'bg-teal-50 border-teal-400 text-teal-800'
              : 'bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-100'
          }`}
        >
          <ImageIcon className="w-4 h-4" />
        </button>

        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={
            isFirstRun
              ? '(Optional) Add specific note, or leave blank and click Approve...'
              : 'e.g., "Bring faces closer to the original", "Reduce remaining glare"...'
          }
          disabled={isProcessing}
          className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 disabled:text-slate-400 disabled:placeholder-slate-300"
        />

        <button
          type="submit"
          disabled={isProcessing || (!isFirstRun && !inputText.trim() && !referenceImage)}
          className="px-3.5 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 bg-teal-600 hover:bg-teal-500 text-white disabled:bg-slate-200 disabled:text-slate-500 disabled:border disabled:border-slate-300"
        >
          {isFirstRun ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Approve &amp; Restore</span>
            </>
          ) : (
            <>
              <Send className="w-3.5 h-3.5" />
              <span>Instruct</span>
            </>
          )}
        </button>
      </form>

      <div className="px-3 py-1 bg-slate-50 text-[10px] text-slate-600 text-center border-t border-slate-200">
        {isFirstRun
          ? isDigital
            ? 'Clicking "Approve & Restore" starts conservative digital cleanup using the confirmed source and identity safeguards.'
            : 'Clicking "Approve & Restore" starts restoration using the confirmed medium and identity safeguards.'
          : isDigital
            ? 'Agent operates conservatively: corrects supported capture defects while safeguarding identity and geometry.'
            : 'Agent operates conservatively: repairs physical damage while strictly safeguarding natural skin softness.'}
      </div>
    </div>
  );
};
